import { useEffect, useMemo, useState } from 'react';
import * as db from '../lib/data.js';
import { siblingTier, planFor, changedFields, diffTail, sharedWords, FILLABLE } from '../lib/siblings.js';
import { itemName } from '../lib/naming.js';
import { typedByPerson } from '../lib/materials.js';
import NameTh from './NameTh.jsx';

// หลังบันทึกใบหนึ่ง — เสนอให้ "ของรุ่นเดียวกัน ต่างแค่สี/ไซส์" ได้ค่าเดียวกัน
//
// ทำไมต้องมี: ใบ SP-261015 มี 114 บรรทัด แต่เป็นของแค่ 36 รุ่น ที่เหลือคือสีอื่นของรุ่นเดิม
//   ซึ่งใช้วัสดุ/พิกัด/คำบรรยายชุดเดียวกันทั้งหมด (วัดจริง 25 ต.ค. 69 → ลดแรงพิมพ์ 68%)
// 28 ต.ค. 69: แก้ช่องไหน (วัสดุ · ผลิตที่ · ชื่อ ฯลฯ) → ให้รุ่นเดียวกัน "เปลี่ยนตาม" ได้ด้วย ไม่ใช่แค่เติมช่องว่าง
//   (กฎละเอียดอยู่ที่ planFor / nameFollow ใน src/lib/siblings.js)
//
// ⚠ กฎที่ห้ามถอด:
//   ① ต้องให้คนเห็นรายชื่อ + สิ่งที่จะเปลี่ยน แล้วติ๊กยืนยันก่อนเขียนทุกครั้ง — กฎจับรุ่นเป็นการเดาจากชื่อ/รหัส
//   ② ทับค่าเดิมได้เฉพาะ "ช่องที่คนเพิ่งแก้ในใบนี้" · แถวส้ม (ค่าเดิมไม่ตรง / ชื่อที่แก้ถึงส่วนสี-ไซส์)
//      ไม่ติ๊กให้เด็ดขาด — ทั้งตอนเปิด · ปุ่มติ๊กทั้งกลุ่ม · ตอนเปิดช่องกลับมา (รีวิว 28 ต.ค.: ปิด-ติ๊กทั้งกลุ่ม-เปิดคืน
//      เคยทำให้แถวส้มค้างติ๊กแล้วถูกเขียนทับ)
//   ③ ไม่ก๊อป hs_confirmed / is_dangerous_goods
//   ④ วัสดุที่มาจากผล AI (material_research_id ยังอยู่) ไม่กระจายจากแผงนี้ — ต้องผ่านหน้าตรวจผลเท่านั้น
//      AI อาจบอกว่าสีอื่นวัสดุไม่เท่ากัน และ SKU นั้นยังรอคนเลือกอยู่ที่นั่น (รีวิว 1 พ.ย. 69 ชี้ให้เห็นว่าเคยติ๊กให้ด้วย)
//      คนแก้ค่าเองแล้ว = trigger ล้าง research id ในใบที่บันทึก → กลับมาใช้ได้ตามปกติ · ช่องอื่นไม่เกี่ยว
//      แต่ SKU ที่อยู่ในงานซึ่ง AI บอกว่าสีวัสดุไม่เท่ากัน = วัสดุเป็นแถบส้ม ไม่ติ๊กให้ (อ่านงานไม่ได้ = ถือว่าทุกแถวเป็นแบบนั้น)

const label = (c) => itemName(c);   // อังกฤษก่อน — ชื่อไทยซ้ำกันทั้งรุ่น แยกแถวไม่ออก (naming.js)
const sku6 = (c) => `${String(c?.brand ?? '')}|${String(c?.sku ?? '').trim().slice(0, 6).toUpperCase()}`;
const CAP = 60;

const KIND = {
  follow:   'เปลี่ยนตาม',
  rename:   'เปลี่ยนชื่อตาม',
  fill:     'เติม',
  override: 'ทับค่าที่ไม่ตรง',
  check:    'ดูก่อนติ๊ก — อาจไม่ใช่ชื่อของรายการนี้',
};
const FLAG = { override: 'ทับ', check: 'ดูก่อน' };   // ป้ายข้อความบนชิป — ไม่พึ่งสีอย่างเดียว
const WHY_VARIANT = 'AI บอกว่าสีในรุ่นนี้วัสดุไม่เท่ากัน';
const WHY_UNKNOWN = 'อ่านผลหาวัสดุด้วย AI ของรุ่นนี้ไม่สำเร็จ';
const holdWhy = (held, o) => (held === null ? WHY_UNKNOWN : held?.has(o.sku) ? WHY_VARIANT : null);

// จำนวนคำ "ชื่อรุ่น" ที่ทั้งรุ่นใช้ร่วมกัน — คิดจาก **ทุกแถวในแผง** (รีวิวรอบสอง: คิดจากแถว "ต่างกันแค่สี/ไซส์"
//   อย่างเดียว บางรุ่นมีแต่ไซส์อื่นสีเดียวกัน → คำสีกลายเป็นชื่อรุ่น แก้สีแล้วลามทั้งชุด · รวมทุกแถวได้ค่าน้อยลง = ปลอดภัยขึ้น)
//   ชื่อที่แก้เลยส่วนนี้ไป = แก้สี/ไซส์ของใบนี้เอง → แถบส้ม (siblings.js nameFollow)
function modelWordsOf(src, list, key) {
  if (!String(src?.[key] ?? '').trim() || !list.length) return Infinity;
  return Math.min(...list.map(o => sharedWords(src[key], o[key] ?? '')));
}

// onBusy(true/false) — ให้หน้าต่างแม่กันปิดระหว่างกำลังเขียน (ปิดกลางทางแล้วงานยังเขียนต่อ แต่ผลหายไม่มีใครเห็น)
export default function SiblingFill({ card: saved, before = null, onClose, onChanged, onEmpty, onBusy }) {
  const aiMaterial = !!saved?.material_research_id;
  // ใบต้นทางที่ไม่นับวัสดุจาก AI — ทุกอย่างในแผงนี้ (แผนเขียน · รายการช่อง · ข้อความ) คิดจากใบนี้
  const card = useMemo(() => (aiMaterial ? { ...saved, material: null } : saved), [saved, aiMaterial]);
  const changes = useMemo(() => changedFields(before, card), [before, card]);
  const [use, setUse] = useState(() => new Set(changes.map(c => c.k)));   // ช่องที่ให้เปลี่ยนตาม (ปิดได้ทีละช่อง)
  const [state, setState] = useState({ loading: true });
  const [checked, setChecked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const ref = before?.id ? before : card;     // หน้าตาก่อนแก้ — ใช้หารุ่น · คิดชื่อรุ่น · โชว์ "ต่างกันที่"

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // หารุ่นเดียวกันจาก "หน้าตาก่อนแก้" เป็นหลัก — ของสีอื่นยังชื่อเหมือนใบเดิม (เช่นแก้ชื่อรุ่นทั้งชื่อ
        //   ถ้าหาจากชื่อใหม่จะไม่เจอใครเลย) + หน้าตาหลังแก้ เผื่อแก้แบรนด์/รหัสให้ตรงกับรุ่นอื่น
        const seeds = [before?.id ? before : null, card].filter(Boolean);
        const uniq = [...new Map(seeds.map(s => [sku6(s), s])).values()];
        const pools = await Promise.all(uniq.map(s => db.listSiblingPool(s)));
        if (!alive) return;
        const byId = new Map();
        for (const p of pools) for (const o of p.pool) byId.set(o.id, o);
        const tierOf = (o) => {
          const t = seeds.map(s => siblingTier(s, o));
          return t.includes('sure') ? 'sure' : t.includes('maybe') ? 'maybe' : null;
        };
        let sure = [], maybe = [];
        for (const o of byId.values()) {
          if (o.id === card.id) continue;
          const t = tierOf(o);
          if (t === 'sure') sure.push(o); else if (t === 'maybe') maybe.push(o);
        }
        const bySku = (a, b) => String(a.sku ?? '').localeCompare(String(b.sku ?? ''));
        sure.sort(bySku); maybe.sort(bySku);
        const truncated = pools.some(p => p.truncated) || sure.length > CAP || maybe.length > CAP;
        sure = sure.slice(0, CAP); maybe = maybe.slice(0, CAP);
        // ไม่มีรุ่นเดียวกันเลย → ไม่ต้องให้คนกดปิดเปล่า ๆ ปิดให้เอง
        if (!sure.length && !maybe.length && onEmpty) { onEmpty(); return; }
        // ชื่อไทยไม่ใช้ชื่อรุ่น — ชื่อสำแดงไทยเป็นคำเดียวกว้าง ๆ ทั้งรุ่น ใช้กฎสี/ไซส์ (VARIANT_TH) แทน
        const model = { name_en: modelWordsOf(ref, [...sure, ...maybe], 'name_en'), name_th: Infinity };
        // null = อ่านไม่ได้ → ไม่ติ๊กวัสดุให้ใครเลย (ปลอดภัยไว้ก่อน แถวยังติ๊กเองได้)
        const held = String(card?.material ?? '').trim()
          ? await db.materialVariantSkus([...sure, ...maybe].map(o => o.sku)).catch(() => null)
          : new Set();
        if (!alive) return;
        setState({ loading: false, sure, maybe, truncated, model, held });
        // ต่างกันแค่สี/ไซส์ + มีอะไรให้เปลี่ยน + ไม่มีแถบส้ม + **ไม่เปลี่ยนชื่ออังกฤษ** → ติ๊กไว้ให้ก่อน
        //   ชื่ออังกฤษไม่ติ๊กให้เลย (รีวิวสองรอบเจอทางที่ชื่อผิดถูกติ๊กให้ 5 แบบ) — คนดูชื่อใหม่ก่อน แล้วติ๊กเอง/ติ๊กทั้งกลุ่ม
        const all = new Set(changes.map(c => c.k));
        setChecked(new Set(sure.filter(o => {
          const p = planFor(card, o, changes, all, { model, tier: 'sure', holdMaterial: holdWhy(held, o) });
          return p.n > 0 && p.overrides === 0 && !p.acts.some(a => a.k === 'name_en');
        }).map(o => o.id)));
      } catch (e) {
        if (alive) { setState({ loading: false, sure: [], maybe: [] }); setError(e.message ?? String(e)); }
      }
    })();
    return () => { alive = false; };
  }, [card?.id]);

  // ช่องที่ยังไม่ได้แก้แต่ใบนี้มี — เติมให้ใบที่ยังว่าง (ของเดิม)
  const giving = useMemo(
    () => FILLABLE.filter(([k]) => String(card?.[k] ?? '').trim() && !changes.some(c => c.k === k)).map(([, th]) => th),
    [card, changes],
  );

  const sureIds = useMemo(() => new Set((state.sure ?? []).map(o => o.id)), [state.sure]);
  const tierOfRow = (o) => (sureIds.has(o.id) ? 'sure' : 'maybe');
  const planWith = (o, u) => planFor(card, o, changes, u, {
    model: state.model ?? {}, tier: tierOfRow(o), holdMaterial: holdWhy(state.held, o),
  });
  const planOf = (o) => planWith(o, use);

  const plan = useMemo(() => {
    const all = [...(state.sure ?? []), ...(state.maybe ?? [])];
    return all.filter(o => checked.has(o.id))
      .map(o => ({ id: o.id, card: o, label: label(o), updated_at: o.updated_at, ...planOf(o) }))
      .filter(p => p.n > 0);
  }, [checked, state, card, changes, use]);
  const nOver = plan.filter(p => p.overrides > 0).length;

  const toggle = (id) => !busy && setChecked(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  // เปิดช่องกลับมา → แถวที่ติ๊กไว้แล้ว "ส้มเพิ่มขึ้น" เพราะช่องนี้ ต้องเอาติ๊กออก ให้คนดูแล้วติ๊กเองใหม่
  //   (แถวส้มที่คนติ๊กเองไว้ก่อนแล้วและช่องนี้ไม่ได้ทำให้ส้มเพิ่ม → คงไว้)
  const toggleField = (k) => {
    if (busy) return;
    const next = new Set(use);
    const turningOn = !next.has(k);
    turningOn ? next.add(k) : next.delete(k);
    if (turningOn) {
      const all = [...(state.sure ?? []), ...(state.maybe ?? [])];
      setChecked(prev => new Set([...prev].filter(id => {
        const o = all.find(x => x.id === id);
        return o && planWith(o, next).overrides <= planWith(o, use).overrides;
      })));
    }
    setUse(next);
  };
  // ติ๊กทั้งกลุ่ม — เฉพาะแถวที่ไม่มีส้ม · แถวส้มต้องดูแล้วติ๊กเองทีละรายการ
  const tickGroup = (list, on) => setChecked(prev => {
    const next = new Set(prev);
    for (const o of list) {
      const p = planOf(o);
      if (!on) next.delete(o.id);
      else if (p.n > 0 && p.overrides === 0) next.add(o.id);
    }
    return next;
  });

  const run = async () => {
    setBusy(true); onBusy?.(true); setError(null);
    try {
      const r = await db.fillSiblings(plan);
      const failedIds = new Set(r.failed.map(f => f.id));
      const byId = new Map(plan.map(p => [p.id, p.card]));
      setResult({
        ...r,
        failed: r.failed.map(f => ({ ...f, card: byId.get(f.id) })),
        // โชว์ชื่อหลังเขียน (แก้ชื่อในรอบเดียวกันก็เห็นชื่อใหม่)
        unconfirmed: plan.filter(p => p.unconfirm && !failedIds.has(p.id)).map(p => ({ ...p.card, ...p.patch })),
      });
      if (r.done) onChanged?.();
    } catch (e) { setError(e.message ?? String(e)); }
    setBusy(false); onBusy?.(false);
  };

  // ⚠ เป็นฟังก์ชันเรนเดอร์ ไม่ใช่ component — ประกาศ component ในตัว component = ทุกแถว remount ทุกครั้งที่ติ๊ก (โฟกัสหลุด)
  const renderRow = (o, dim) => {
    const p = planOf(o);
    const d = diffTail(ref, o);
    const none = p.n === 0;
    const count = (kinds) => p.acts.filter(a => kinds.includes(a.kind)).length;
    const nChange = count(['follow', 'rename']);
    const nFill = count(['fill']);
    const nOverride = count(['override']);
    const nCheck = count(['check']);
    const skipNote = p.skips.length ? `${p.skips.join(' · ')}ไม่ได้เปลี่ยนตาม (ชื่อเดิมไม่ตรงกับใบนี้)` : '';
    const offNote = changes.some(c => !use.has(c.k));
    const summary = none ? (skipNote || (p.heldWhy ? 'ไม่มีช่องให้เติม นอกจากวัสดุ' : offNote ? 'ไม่มีอะไรเปลี่ยน (ปิดช่องที่แก้ไว้)' : 'ตรงกับใบนี้แล้ว')) : [
      nChange && `เปลี่ยน ${nChange}`, nFill && `เติม ${nFill}`,
      nOverride && `ทับค่าที่ไม่ตรง ${nOverride}`, nCheck && `ดูก่อนติ๊ก ${nCheck}`,
    ].filter(Boolean).join(' · ');
    return (
      <label key={o.id} className={`sib-row${none ? ' full' : ''}${p.overrides ? ' warn' : ''}`}>
        <input type="checkbox" disabled={none || !!result || busy} checked={!none && checked.has(o.id)} onChange={() => toggle(o.id)}/>
        <span className="gap-sku">{o.sku || '—'}</span>
        <span className="gap-nm"><NameTh c={o}/></span>
        <span className="sib-diff">{dim ? `ต่างกันที่ ${d.from} → ${d.to}` : `สี/ไซส์ ${d.to}`}</span>
        <span className="gap-miss">{summary}</span>
        {(!none || p.heldWhy) && (
          <span className="sib-acts">
            {p.acts.map(a => (
              <span key={a.k} className={`sib-act ${a.kind}`} title={`${a.why ?? KIND[a.kind]} — ${a.th}: ${a.from || '(ว่าง)'} → ${a.to}`}>
                {FLAG[a.kind] && <b className="sib-flag">{FLAG[a.kind]}</b>}
                <b>{a.th}</b>{' '}
                {a.from ? <><span className="sib-old">{a.from}</span> → </> : 'เติม '}
                <span className="sib-new">{a.to}</span>
                {a.why && <> · {a.why} ฝากเทียบฉลากก่อนติ๊ก</>}
              </span>
            ))}
            {p.unconfirm && <span className="sib-act override"><b className="sib-flag">ทับ</b><b>พิกัด</b> เปลี่ยน → รอ broker ยืนยันพิกัดใหม่</span>}
            {skipNote && <span className="sib-act skip">{skipNote}</span>}
            {p.heldWhy && <span className="sib-act skip">วัสดุไม่เติมให้ — {p.heldWhy} ถ้าจะใส่ ฝากแก้ที่ใบนั้นหลังเทียบฉลาก</span>}
          </span>
        )}
      </label>
    );
  };

  if (state.loading) return <div className="fhint" style={{ padding: '14px 0' }}>กำลังหาของรุ่นเดียวกัน…</div>;

  const nSure = (state.sure ?? []).length;
  const nMaybe = (state.maybe ?? []).length;
  const verb = changes.length ? 'อัปเดต' : 'เติมให้';

  if (!nSure && !nMaybe) {
    return (
      <div className="sib">
        <div className="gap-msg">{error ? 'บันทึกแล้ว ✓ — แต่หาของรุ่นเดียวกันไม่สำเร็จ' : 'บันทึกแล้ว ✓ — ไม่พบของรุ่นเดียวกันในคลัง'}</div>
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot"><button type="button" className="btn1" onClick={onClose}>ปิด</button></div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="sib">
        <div className="gap-msg">
          {verb}แล้ว {result.done} รายการ
          {result.failed.length > 0 && ` · ทำไม่ได้ ${result.failed.length} รายการ`}
        </div>
        {result.unconfirmed.length > 0 && (
          <>
            <div className="gap-bs">
              พิกัดของ {result.unconfirmed.length} รายการนี้เปลี่ยนไป — เอาเครื่องหมาย “broker ยืนยันแล้ว” ออกให้แล้ว
              ฝากส่งให้ broker ช่วยยืนยันพิกัดใหม่:
            </div>
            <div className="gap-list">
              {result.unconfirmed.map(c => (
                <div key={c.id} className="sib-fail"><span className="gap-sku">{c.sku || '—'}</span><NameTh c={c}/></div>
              ))}
            </div>
          </>
        )}
        {result.failed.length > 0 && (
          <div className="gap-list">
            {result.failed.map(f => (
              <div key={f.id} className="sib-fail">
                <span className="gap-sku">{f.card?.sku || '—'}</span>
                <span><NameTh c={f.card} fallback={f.label}/><span className="sib-fail-msg">{f.msg}</span></span>
              </div>
            ))}
          </div>
        )}
        <div className="mfoot"><button type="button" className="btn1" onClick={onClose}>ปิด</button></div>
      </div>
    );
  }

  const tickLabel = changes.length ? 'ติ๊กทุกรายการที่ไม่มีแถบส้ม' : 'ติ๊กทุกรายการที่ยังมีช่องว่างให้เติม';
  const groupHead = (title, list) => (
    <div className="sib-h">
      <span>{title}</span>
      <span className="sib-hbtn">
        <button type="button" className="sib-link" disabled={busy} onClick={() => tickGroup(list, true)}>{tickLabel}</button>
        <button type="button" className="sib-link" disabled={busy} onClick={() => tickGroup(list, false)}>เอาติ๊กออกทั้งหมด</button>
      </span>
    </div>
  );

  return (
    <div className="sib">
      <div className="gap-msg">บันทึกแล้ว ✓</div>
      <div className="gap-bt" style={{ color: '#1f4fd8' }}>
        {changes.length ? `ให้ของรุ่นเดียวกัน ${nSure + nMaybe} รายการเปลี่ยนตามไหม?` : `ของรุ่นเดียวกันในคลัง ${nSure + nMaybe} รายการ`}
      </div>

      {changes.length > 0 && (
        <div className="sib-fields">
          <div className="fhint">ใบนี้เพิ่งแก้ — ติ๊กช่องที่อยากให้รุ่นเดียวกันเปลี่ยนตาม:</div>
          {changes.map(c => (
            <label key={c.k} className="sib-field">
              <input type="checkbox" checked={use.has(c.k)} disabled={busy} onChange={() => toggleField(c.k)}/>
              <b>{c.th}</b>{' '}
              {c.delta
                ? <>{c.delta.from ? <span className="sib-old">{c.delta.from}</span> : 'เพิ่มคำ'}
                    {' → '}<span className="sib-new">{c.delta.to || '(ลบคำนี้ออก)'}</span>
                    <span className="fhint"> (เปลี่ยนเฉพาะส่วนนี้ สี/ไซส์ของแต่ละรายการคงเดิม)</span></>
                : <>{c.from ? <><span className="sib-old">{c.from}</span> → </> : 'เติม '}<span className="sib-new">{c.to}</span></>}
            </label>
          ))}
        </div>
      )}

      <div className="gap-bs">
        {changes.length
          ? <>ติ๊กรายการที่ใช้ค่าเดียวกับใบนี้ แล้วกดปุ่มด้านล่าง · <b>แถบส้ม</b> = ค่าเดิมไม่ตรงกับใบนี้
              หรือชื่อที่อาจเป็นสี/ไซส์ของใบนี้เอง — ไม่ได้ติ๊กไว้ให้ ดูแล้วติ๊กเองถ้าจะใช้
              {giving.length > 0 && <> · ช่อง {giving.join(' · ')} เติมให้เฉพาะรายการที่ยังว่าง</>}</>
          : giving.length
            ? <>ใบนี้มี <b>{giving.join(' · ')}</b> ที่ของสีอื่น/ไซส์อื่นในรุ่นเดียวกันใช้ค่าเดียวกันได้ —
                เลือกรายการที่ใช้ค่าเดียวกัน แล้วกดเติม ของที่กรอกไว้แล้วจะไม่ถูกทับ</>
            : <>ใบนี้ยังไม่มีช่องไหนกรอกไว้ จึงยังไม่มีอะไรแจกให้รุ่นเดียวกัน</>}
      </div>
      {aiMaterial && <div className="fhint">{typedByPerson(saved) ? 'วัสดุของใบนี้พิมพ์จากหน้าตรวจผล' : 'วัสดุของใบนี้มาจากผล AI'} จึงไม่เติมให้จากตรงนี้ — ถ้าจะใช้กับสีอื่น ฝากช่วยเลือกที่หน้า “วัสดุจาก AI”</div>}
      {giving.includes('วัสดุ') && [...(state.sure ?? []), ...(state.maybe ?? [])].some(o => planOf(o).heldWhy) && (
        <div className="fhint">วัสดุเติมให้เฉพาะรายการที่ไม่ได้ขึ้นว่า “วัสดุไม่เติมให้”</div>
      )}
      {state.held === null && (
        <div className="fhint">{changes.some(c => c.k === 'material')
          ? 'อ่านผลหาวัสดุด้วย AI ไม่สำเร็จ จึงไม่ติ๊กวัสดุให้รายการไหน — ฝากเทียบฉลากก่อนใช้ค่าเดียวกัน'
          : 'อ่านผลหาวัสดุด้วย AI ไม่สำเร็จ จึงไม่เติมวัสดุให้รายการไหน — ถ้าจะใส่ ฝากแก้ที่ใบนั้นหลังเทียบฉลาก'}</div>
      )}
      {state.truncated && <div className="gap-msg">รายการในคลังมากกว่าที่แสดง — รุ่นที่ไม่ขึ้นให้ค้นจากหน้าสินค้าแล้วแก้เอง</div>}

      {nSure > 0 && (
        <>
          {groupHead(changes.length ? 'ต่างกันแค่สี/ไซส์ — ติ๊กไว้ให้แล้ว (ยกเว้นแถบส้มและแถวที่เปลี่ยนชื่ออังกฤษ)' : 'ต่างกันแค่สี/ไซส์ — ติ๊กไว้ให้แล้ว', state.sure)}
          <div className="gap-list">{state.sure.map(o => renderRow(o, false))}</div>
        </>
      )}
      {nMaybe > 0 && (
        <>
          {groupHead('รุ่นใกล้เคียง — ติ๊กเองเฉพาะที่ใช้ค่าเดียวกัน', state.maybe)}
          <div className="gap-list">{state.maybe.map(o => renderRow(o, true))}</div>
        </>
      )}

      {error && <div className="ferr">{error}</div>}
      <div className="mfoot spread">
        <span className="mfoot-l fhint">
          ติ๊กอยู่ {plan.length} รายการ{nOver > 0 && <b className="sib-warn-n"> · ในนี้มีแถบส้ม {nOver} รายการ</b>} · ช่องอื่นของรายการเหล่านี้ไม่ถูกแตะ
        </span>
        <span className="mfoot-r">
          <button type="button" className="btn2" disabled={busy} onClick={onClose}>{changes.length ? 'ไม่ต้องเปลี่ยนตาม' : 'ไม่ต้องเติม'}</button>
          <button type="button" className="btn1" disabled={busy || !plan.length} onClick={run}>
            {busy ? 'กำลังบันทึก…' : `${verb} ${plan.length} รายการ${nOver ? ` (รวมแถบส้ม ${nOver})` : ''}`}
          </button>
        </span>
      </div>
    </div>
  );
}
